import { useState } from "react";
import { Check, ChevronsUpDown, Truck } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";

// Safe fallback equipment list (used only if the Transporeon metadata API is
// unreachable) so the user can always pick a container/reefer/truck type.
// "veh:" uids are treated as non-structured on the backend (sent in the comment),
// which keeps the create request valid regardless.
export const FALLBACK_EQUIPMENT = [
  "20ft Dry Container", "40ft Dry Container", "40ft High Cube Container",
  "45ft High Cube Container", "20ft Reefer Container", "40ft Reefer Container",
  "40ft High Cube Reefer Container", "20ft Open Top", "40ft Open Top",
  "20ft Flat Rack", "40ft Flat Rack", "13.6 curtain trailer (Tautliner)",
  "Mega trailer", "Box trailer", "Reefer / Fridge trailer",
  "Frigo double deck trailer", "Flatbed trailer", "Jumbo trailer",
  "Tanker (liquid)", "Tautliner Coil", "Tractor only (power only)",
].map((n) => ({ uid: "veh:" + n, name: n, short: "" }));

// Searchable dropdown for Transporeon lists (equipment/unit types, commodities...).
// options: [{ uid, name, short }]
export const EquipmentSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select container, truck or unit type",
  icon: Icon = Truck,
  testIdPrefix = "equipment",
}) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.uid === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={`${testIdPrefix}-type-trigger`}
          className="w-full justify-between font-normal h-10"
        >
          <span className="flex items-center gap-2 truncate">
            <Icon className="h-4 w-4 shrink-0 opacity-60" />
            <span className="truncate">{selected ? selected.name : placeholder}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search..." data-testid={`${testIdPrefix}-type-search`} />
          <CommandList>
            <CommandEmpty>Nothing found.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.uid}
                  value={o.name}
                  data-testid={`${testIdPrefix}-option-${o.uid}`}
                  onSelect={() => {
                    onChange(o.uid === value ? "" : o.uid, o.uid === value ? "" : o.name);
                    setOpen(false);
                  }}
                >
                  <Check className={`mr-2 h-4 w-4 ${o.uid === value ? "opacity-100" : "opacity-0"}`} />
                  <span className="flex-1">{o.name}</span>
                  {o.short ? <span className="text-xs text-muted-foreground ml-2">{o.short}</span> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
